import { TaskSubTicketResource } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources';
import { TimeTrackingSessionResource } from 'dy-frontend-http-repository/lib/modules/TimeTacking/resources';
import React, { useState } from 'react';

import RouterLink from '@app/components/RouterLink';
import DevText from '@app/components/Text';
import { Endpoints } from '@app/data/consts';
import { Button, Callout, Colors, FormGroup, Intent, Spinner, TextArea } from '@blueprintjs/core';
import Flex from '@components/Flex';
import Heading from '@components/Heading';

import useElapsedTimer from '../../hooks/useElapsedTimer';
import useSessionHeartbeat from '../../hooks/useSessionHeartbeat';
import { ReportCustody, TimerOperationResult } from '../../store/timerOperations';
import { formatElapsedDuration, formatLoggedMinutes } from '../../utils';

// Matches the existing work report validator; the backing column is VARCHAR(255)
const MAX_COMMENT_LENGTH = 250;

export interface TimerControlProps {
    subTicket: TaskSubTicketResource;

    // The caller's single open session anywhere in the product, or null once restoration is done
    activeSession: TimeTrackingSessionResource | null;

    // Restoration in flight. Distinct from "no session": starting on the first reading would
    // double-start on the second
    isActiveSessionLoading: boolean;

    // The restoration read failed. Not the same as a confirmed absence of a session, so Start
    // stays withheld rather than being offered over a session that may well exist.
    hasActiveSessionError: boolean;

    canStartTimer: boolean;
    canStopTimer: boolean;
    canStopThenStartHere: boolean;

    isBusy: boolean;
    errorMessage: string | null;

    // Who holds the words the designer typed, for the operation that just failed. A stop that did
    // not commit really has kept the text; a stop that DID commit saved it against the session it
    // closed and emptied the box, so telling that designer it was "kept" sends them looking for
    // words that are not there. A plain Start had no report at all.
    reportCustody: ReportCustody;

    // Minutes the server logged for that committed stop, or null when nothing was stopped.
    //
    // This is the fact a designer whose switch half-failed most wants and is least able to check:
    // their previous session is gone from the screen, so how much it banked is only knowable if
    // it is said here. It is stated for EVERY committed stop, whatever became of the new start
    // and whether or not the list could be re-read afterwards.
    loggedDurationMin: number | null;

    // Human label of the sub-ticket the other timer is on, once resolved
    runningElsewhereLabel: string | null;
    runningElsewhereTaskId: ID | null;

    onStart: () => void;

    // Both return the structured write result rather than a bare boolean. The question this
    // component has to answer is "did the session these words belong to actually close?", which
    // is `didStop` — not whether the whole operation succeeded, and never whether the views
    // could be re-read afterwards.
    onStopAndStartHere: (previousSessionComment: string) => Promise<TimerOperationResult>;
    onStop: (comment: string) => Promise<TimerOperationResult>;

    onHeartbeat: (sessionId: ID) => void;
    onDismissError: () => void;
}

export type Props = TimerControlProps;

const TimerControl: React.FC<Props> = ({
    subTicket,
    activeSession,
    isActiveSessionLoading,
    hasActiveSessionError,
    canStartTimer,
    canStopTimer,
    canStopThenStartHere,
    isBusy,
    errorMessage,
    reportCustody,
    loggedDurationMin,
    runningElsewhereLabel,
    runningElsewhereTaskId,
    onStart,
    onStopAndStartHere,
    onStop,
    onHeartbeat,
    onDismissError,
}) => {
    // Kept in component state and cleared ONLY after the server confirms the stop. A failed
    // stop that wiped the box would destroy the only copy of what the designer just typed.
    const [comment, setComment] = useState('');
    const [handoffComment, setHandoffComment] = useState('');
    const [isHandoffConfirming, setIsHandoffConfirming] = useState(false);

    const isRunningHere = activeSession !== null && activeSession.task_sub_ticket_id === subTicket.id;
    const isRunningElsewhere = activeSession !== null && activeSession.task_sub_ticket_id !== subTicket.id;

    // Elapsed time comes from the server's `started_at`, never from a local start instant
    const elapsedSeconds = useElapsedTimer(isRunningHere ? activeSession.started_at : null);

    // Registered once while a timer runs here; cleared on stop, unmount and tab close
    useSessionHeartbeat(isRunningHere ? activeSession.id : null, onHeartbeat);

    // Exactly one sentence per custody, and none at all when no report was in play
    const renderReportCustody = () => {
        if (reportCustody === 'retained') {
            return (
                <DevText className="mt-1" muted>
                    Your report text has been kept — you can try again.
                </DevText>
            );
        }

        if (reportCustody === 'saved') {
            // One sentence, carrying both halves of what happened to the previous session: the
            // report went with it, and this is how much it logged
            return (
                <DevText className="mt-1" muted>
                    Your report for the previous timer was saved against it and cleared from the box
                    {loggedDurationMin === null ? '' : ` — ${formatLoggedMinutes(loggedDurationMin)} logged`}.
                </DevText>
            );
        }

        return null;
    };

    const renderError = () => {
        if (!errorMessage) {
            return null;
        }

        return (
            <Callout className="mb-2" intent={Intent.DANGER} title="That did not go through">
                <DevText>{errorMessage}</DevText>
                {renderReportCustody()}
                <Button className="mt-1" minimal onClick={onDismissError}>
                    Dismiss
                </Button>
            </Callout>
        );
    };

    const renderRunning = () => (
        <div>
            <Flex direction="row" align="center" justify="space-between">
                <Heading type="h3">{formatElapsedDuration(elapsedSeconds)}</Heading>
                <DevText muted>Timer running</DevText>
            </Flex>

            <FormGroup className="mt-2" label="Work report">
                <TextArea
                    fill
                    growVertically
                    disabled={isBusy}
                    maxLength={MAX_COMMENT_LENGTH}
                    placeholder="What did you work on?"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                />
            </FormGroup>

            <Button
                loading={isBusy}
                icon="stop"
                intent={Intent.DANGER}
                onClick={async () => {
                    // Clear only on a confirmed stop; the server's stop is idempotent, so a
                    // retry after a timeout that actually landed returns the same entry
                    const result = await onStop(comment);
                    if (result.didStop) {
                        setComment('');
                    }
                }}
            >
                Stop &amp; Save
            </Button>
        </div>
    );

    // One global active timer. Starting here means finalising the other session first, which is
    // real work being banked against a different phase — so it needs its own report text and an
    // explicit confirmation, never a single silent button.
    const renderRunningElsewhere = () => {
        const label = runningElsewhereLabel ?? 'another sub-ticket';

        const renderLink = () => {
            if (!runningElsewhereTaskId) {
                return <DevText inline>{label}</DevText>;
            }

            return (
                <RouterLink
                    color={Colors.WHITE}
                    to={Endpoints.TASK_SUB_TICKETS.replace(':taskId', runningElsewhereTaskId)}
                >
                    {label}
                </RouterLink>
            );
        };

        return (
            <Callout intent={Intent.WARNING} title="A timer is already running">
                <DevText>
                    Your timer is running on {renderLink()}. Only one timer can run at a time.
                </DevText>

                {!canStopThenStartHere && (
                    <DevText className="mt-1" muted>
                        Stop it from that sub-ticket to start one here.
                    </DevText>
                )}

                {canStopThenStartHere && !isHandoffConfirming && (
                    <Button className="mt-1" disabled={isBusy} onClick={() => setIsHandoffConfirming(true)}>
                        Stop it and start here
                    </Button>
                )}

                {canStopThenStartHere && isHandoffConfirming && (
                    <div className="mt-1">
                        <FormGroup
                            label={`Work report for ${label}`}
                            helperText="This finalises the timer that is running now — it is saved against that phase, not this one."
                        >
                            <TextArea
                                fill
                                growVertically
                                disabled={isBusy}
                                maxLength={MAX_COMMENT_LENGTH}
                                placeholder="What did you work on there?"
                                value={handoffComment}
                                onChange={(e) => setHandoffComment(e.target.value)}
                            />
                        </FormGroup>

                        <Flex direction="row" align="center">
                            <Button
                                className="mr-1"
                                loading={isBusy}
                                intent={Intent.WARNING}
                                onClick={async () => {
                                    const result = await onStopAndStartHere(handoffComment);

                                    // Keyed on the STOP, not on the switch succeeding. Once the
                                    // previous session is finalised, this text has been banked
                                    // against it and belongs to no other session — leaving it in
                                    // the box would let the next Confirm submit one phase's
                                    // report against a different phase. The start failure stays
                                    // on screen in the error callout regardless.
                                    if (result.didStop) {
                                        setHandoffComment('');
                                        setIsHandoffConfirming(false);
                                    }
                                }}
                            >
                                Confirm: stop that timer &amp; start here
                            </Button>

                            <Button minimal disabled={isBusy} onClick={() => setIsHandoffConfirming(false)}>
                                Cancel
                            </Button>
                        </Flex>
                    </div>
                )}
            </Callout>
        );
    };

    const renderIdle = () => {
        if (!canStartTimer) {
            return <DevText muted>No timer running</DevText>;
        }

        if (hasActiveSessionError) {
            return (
                <Callout intent={Intent.WARNING} title="Could not check for a running timer">
                    <DevText>
                        Your timer status could not be read, so starting one here is withheld to avoid
                        starting a second. Reload to try again.
                    </DevText>
                </Callout>
            );
        }

        return (
            <Flex direction="row" align="center" justify="space-between">
                <DevText muted>No timer running</DevText>

                <Button
                    loading={isBusy}
                    // `renderBody` already withholds this whole region while restoration is in
                    // flight, so this is belt and braces: it keeps Start unusable if that branch
                    // order is ever changed, rather than being the only thing standing between a
                    // designer and a second concurrent session.
                    disabled={isActiveSessionLoading}
                    icon="play"
                    intent={Intent.SUCCESS}
                    onClick={onStart}
                >
                    Start timer
                </Button>
            </Flex>
        );
    };

    const renderRestoring = () => (
        <Flex direction="row" align="center">
            <Spinner className="mr-1" size={16} />
            <DevText muted>Restoring timer…</DevText>
        </Flex>
    );

    const renderBody = () => {
        if (isRunningHere) {
            // The one control that survives a closed parent: banking work already done
            return canStopTimer ? renderRunning() : <DevText muted>A timer is running on this phase.</DevText>;
        }

        if (isActiveSessionLoading) {
            return renderRestoring();
        }

        if (isRunningElsewhere) {
            return renderRunningElsewhere();
        }

        return renderIdle();
    };

    return (
        <div>
            {renderError()}
            {renderBody()}
        </div>
    );
};

export default TimerControl;
